import axios from 'axios';
import {
  BulkStatusUpdateRequest,
  BulkStatusUpdateResponse,
  CustomerInfo,
  ServiceConfig,
  ServiceRequestDetail,
  ServiceRequestSummary,
} from './types';
import { BASE_URL, CUSTOMER_NAME_PLACEHOLDER } from './const';
import { OrderType,OrderAge } from './util';
import { STATUS_IDS, STATUS_OPTIONS } from './statusConfig';
import type { StatusId } from './statusConfig';
import { QuickViewResponse, StatusCountRequest } from 'components/QuickView/type';
export { STATUS_OPTIONS, STATUS_IDS, STATUS_DEFINITIONS } from './statusConfig';
export type { StatusId, StatusKey, StatusDefinition } from './statusConfig';
export const StatusIds = STATUS_IDS;
export const StatusOptions = STATUS_OPTIONS;

export interface UpdateCartRequest {
  productId: string;
  priceSourceId: string;
  priceSource: string;
  attributes?: {
    key: string;
    value: string;
  }[];
  cartAddOns: {
    productId: string;
    priceSourceId: string;
    priceSource: string;
    attributes?: {
      key: string;
      value: string;
    }[];
  }[];
}

export interface AddOnItem {
  service_guid: string;
  service_sku: string;
  status?: string;
  location_type?: string;
  service_info_1?: string;
  service_value_1?: string;
}

export interface ServiceItem {
  service_item_guid: string;
  sku: string;
  tag?: string;
  comments?: string;
  photo?: string;
  services: AddOnItem[];
  item_location: string;
  item_location_type: string;
  contact_by_email?: boolean;
  contact_by_phone?: boolean;
}

export interface UploadImageRequest {
  contentType: string;
  contentLength: number;
}

export interface UpdateServiceRequest {
  serviceItemId?: string;
  status: StatusId;
  associateId: string;
  associateName: string;
  associateEmail?: string;
  service?: { service_guid: string; status: StatusId }[];
  // Optional metadata for extended actions
  reasonCode?: string; // e.g., ADMIN_ERROR, NO_PICKUP
  hold_comment?: string; // comment when putting on hold
  cancel_comment?: string; // comment when cancelling
  deliveryId?: string; // for delivery received flows (optional)
  storeId?: string // for delivery received flow(optional)
  requestType?: string // to capture associate name and time
}

export interface UploadImageResponse {
  imageId: string;
  uploadUrl: string;
}

export interface TagValidationResponse {
  unique: boolean;
}

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const DEFAULT_CUSTOMER_NAME = CUSTOMER_NAME_PLACEHOLDER;

function normalizeCustomerName<T extends { customerName?: string | null }>(entity: T): T {
  const trimmed = entity.customerName?.trim();
  return {
    ...entity,
    customerName: trimmed && trimmed.length > 0 ? trimmed : DEFAULT_CUSTOMER_NAME,
  } as T;
}

async function withRetry<T>(fn: () => Promise<T>, attempts = 3, baseDelayMs = 300): Promise<T> {
  let attempt = 0;
  let lastError: any;

  while (attempt < attempts) {
    try {
      return await fn();
    } catch (err: any) {
      lastError = err;
      const status = err?.response?.status as number | undefined;
      const shouldRetry = !err?.response || RETRYABLE_STATUS.has(status ?? 0);
      if (!shouldRetry || attempt === attempts - 1) {
        throw err;
      }
      const delay = baseDelayMs * Math.pow(2, attempt);
      await sleep(delay);
      attempt += 1;
    }
  }

  throw lastError;
}

const normalizeServiceConfig = (config: any): ServiceConfig => {
  const locationServiced = Array.isArray(config.locationServiced)
    ? config.locationServiced
    : Array.isArray(config.location_serviced)
      ? config.location_serviced
      : [];

  const toNumberOrNull = (value: any): number | null => {
    if (value == null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const requiredInfo = (config.requiredInfo ?? config.required_info ?? [])
    .map((info: any) => ({
      label: info.label ?? '',
      valueType: info.valueType ?? info.value_type ?? null,
      valueOptions: info.valueOptions ?? info.value_options ?? null,
      regex: info.regex ?? null,
      regexError: info.regexError ?? info.regex_error ?? null,
      minValue: toNumberOrNull(info.minValue ?? info.min_value),
      defaultValue: toNumberOrNull(info.defaultValue ?? info.default_value),
    }))
    .filter((info: any) => info.label);

  const rawSortOrder = config.sortOrder ?? config.sort_order ?? null;
  const sortOrder =
    rawSortOrder != null && !Number.isNaN(Number(rawSortOrder)) ? Number(rawSortOrder) : null;

  return {
    id: config.id,
    sku: config.sku,
    description: config.description,
    metal: config.metal,
    cost: Number(config.cost ?? 0),
    allowQuantity: Boolean(config.allowQuantity ?? config.allow_quantity ?? false),
    sortOrder,
    locationServiced,
    icon: config.icon ?? undefined,
    requiredInfo,
  };
};

export async function getServiceConfigs(token: string): Promise<ServiceConfig[]> {
  const { data } = await withRetry(() =>
    axios.get(`${BASE_URL}/service-configs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return Array.isArray(data) ? data.map(normalizeServiceConfig) : [];
}

export async function getCustomerInfo(token: string, cartId: string): Promise<CustomerInfo> {
  const response = await withRetry(() =>
    axios.get<CustomerInfo>(`${BASE_URL}/customer/cart/${cartId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return response.data;
}

export async function updateCart(token: string, cartId: string, request: UpdateCartRequest): Promise<void> {
  await withRetry(() =>
    axios.post<void>(`${BASE_URL}/cart/${cartId}`, request, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
}

export async function uploadImage(token: string, request: UploadImageRequest): Promise<UploadImageResponse> {
  try {
    const response = await withRetry(() =>
      axios.post<UploadImageResponse>(`${BASE_URL}/upload/image`, request, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
    );
    return response.data;
  } catch (error: any) {
    console.error('Upload error: ' + JSON.stringify(error?.response) || JSON.stringify(error.message) || JSON.stringify(error.code));
    throw error;
  }
}

export async function isTagUnique(token: string, tag: string): Promise<boolean> {
  const response = await withRetry(() =>
    axios.get<TagValidationResponse>(`${BASE_URL}/tags/${encodeURIComponent(tag)}/unique`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return Boolean(response.data?.unique);
}

export async function getServiceRequestsByStatusAndStoreId(
  token: string,
  statusIds?: string | number, // <-- CSV like "2,4" or a single id, or undefined
  storeId?: string,
  limit?: number,
  offset?: number,
  location?: 'Store' | 'Kerrville' | 'Kerrville,Store' | 'Kerrville_all' | 'Store_all',
  type?: OrderType,
  age?: OrderAge,
  sortBy?:string,
  sortOrder?:string
): Promise<ServiceRequestSummary[]> {
  const params = new URLSearchParams();
  if (storeId) params.set('storeId', storeId);
  if (typeof limit === 'number') params.set('limit', String(limit));
  if (typeof offset === 'number') params.set('offset', String(offset));
  if (statusIds != null && String(statusIds).trim()) params.set('statusId', String(statusIds)); // <-- key line
  if (location) params.set('location', location);
  if (type) params.set('type', type);
  if (age) params.set('age', age);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  
  const response = await withRetry(() =>
    axios.get<ServiceRequestSummary[]>(
      `${BASE_URL}?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
  );
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map(normalizeCustomerName);
}

// Fetch order detail by primary id (serviceItemId)
export async function getServiceRequestsByOrderId(token: string, id: string): Promise<ServiceRequestDetail> {
  const response = await withRetry(() =>
    axios.get<ServiceRequestDetail>(`${BASE_URL}/orders/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return normalizeCustomerName(response.data);
}

export async function updateStatus(token: string, request: UpdateServiceRequest): Promise<void> {
  await withRetry(() =>
    axios.put<void>(`${BASE_URL}`, request, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
  );
}

export async function bulkStatusUpdate(
  token: string,
  request: BulkStatusUpdateRequest
): Promise<BulkStatusUpdateResponse> {

  const response = await withRetry(() =>
    axios.put<BulkStatusUpdateResponse>(`${BASE_URL}/bulk-update`, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
  );

  return response.data;
}

export type CancelReason = 'ADMIN_ERROR' | 'NO_PICKUP';

export interface UpdateStatusBase {
  serviceItemId: string;
  associateId: string;
  associateName: string;
}

export async function startService(token: string, req: UpdateStatusBase): Promise<void> {
  return updateStatus(token, { ...req, status: STATUS_IDS.START });
}

export async function stopService(token: string, req: UpdateStatusBase): Promise<void> {
  return updateStatus(token, { ...req, status: STATUS_IDS.STOP });
}

export async function markReadyForPickup(token: string, req: UpdateStatusBase): Promise<void> {
  return updateStatus(token, { ...req, status: STATUS_IDS.READY_TO_PICKUP });
}


export async function recordDeliveryAndMarkReceived(
  token: string,
  req: UpdateStatusBase & { deliveryId: string }
): Promise<void> {
  return updateStatus(token, {
    ...req,
    status: STATUS_IDS.RECEIVED_BY_KERRVILLE,
    deliveryId: req.deliveryId,
  });
}

// Fetch a service request by Delivery ID scanned from courier
export async function getServiceRequestByDeliveryId(
  token: string,
  deliveryId: string
): Promise<ServiceRequestDetail> {
  const response = await withRetry(() =>
    axios.get<ServiceRequestDetail>(
      `${BASE_URL}/orders/delivery/${encodeURIComponent(deliveryId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
  );
  return normalizeCustomerName(response.data);
}


export async function searchServiceRequests(
  token: string,
  statusId: string | undefined,
  storeId: string,
  q: string,
  location?: 'Store' | 'Kerrville' | 'Kerrville,Store',
  type?: OrderType,
  limit?:string,
  offset?:string,
  age?: OrderAge,
  sortBy?:string,
  sortOrder?:string
): Promise<ServiceRequestSummary[]> {
  const params = new URLSearchParams({ storeId, q });
  if (statusId) params.set('statusId', statusId);
  if (location) params.set('location', location);
  if (type) params.set('type', type);
  if (age) params.set('age', age);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);
  
  if (limit) params.set('limit', limit);
  if (offset) params.set('offset', offset);

  const response = await axios.get<ServiceRequestSummary[]>(`${BASE_URL}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  }
  );
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map(normalizeCustomerName);
}

// Shared status options (id/label) used by filters and UI
// Global search across fields, optional CSV status filter
export async function searchGlobalServiceRequests(
  token: string,
  storeId: string,
  q: string,
  statusIdsCsv?: string
): Promise<ServiceRequestSummary[]> {
  const params = new URLSearchParams({ storeId, q });
  if (statusIdsCsv) params.set('statusId', statusIdsCsv);
  const response = await axios.get<ServiceRequestSummary[]>(`${BASE_URL}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map(normalizeCustomerName);
}

// Bulk update tracking numbers for multiple orders
export interface TrackingUpdateItem {
  service_item_id: string;
  tracking_reference: string;
}

export interface BulkTrackingUpdatePayload {
  data: TrackingUpdateItem[];
  associate_id: string;
  associate_name: string;
}

export async function bulkUpdateTracking(token: string, payload: BulkTrackingUpdatePayload): Promise<void> {
  await withRetry(() =>
    axios.post<void>(`${BASE_URL}/orders/tracking`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
  );
}

export async function getProfile(token: string, profileId: string): Promise<CustomerInfo> {
  const response = await withRetry(() =>
    axios.get<CustomerInfo>(`${BASE_URL}/profiles/${profileId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );
  return response.data;
}

export async function getFilteredStatusCount(
  token: string,
  request: StatusCountRequest
): Promise<QuickViewResponse> {
  const response = await withRetry(() =>
    axios.post<QuickViewResponse>(`${BASE_URL}/statuscount`, request, {
      headers: { Authorization: `Bearer ${token}` },
    })
  );

  return response.data;
}

export async function getStatusCount(
  token: string,
  storeId: string
): Promise<QuickViewResponse> {
  const response = await withRetry(() =>
    axios.post<QuickViewResponse>(
      `${BASE_URL}/statuscount`,
      { storeId },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
  );

  return response.data;
}

